import React from 'react';
import { Alert, Button, Input, Tabs, notification } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetMcpConnectionInfoQuery } from '../../../services/oauthApi';

// "Connect an AI assistant (MCP)": the Workflo MCP server address and short,
// copy-paste instructions per AI client. The address comes from the backend
// (MCP_RESOURCE_URL), so it always matches what the server accepts.

const isLocalAddress = (url) => {
  try {
    return ['localhost', '127.0.0.1', '[::1]'].includes(new URL(url).hostname);
  } catch {
    return false;
  }
};

const CodeBlock = ({ code, onCopy }) => (
  <div className="relative group">
    <pre
      className="text-xs rounded-lg p-3 pr-20 overflow-x-auto whitespace-pre"
      style={{ background: '#0f172a', color: '#e2e8f0', margin: 0 }}
    >
      {code}
    </pre>
    <Button size="small" className="absolute top-2 right-2" onClick={() => onCopy(code)}>
      <FormattedMessage id="MCP_COPY" defaultMessage="Copy" />
    </Button>
  </div>
);

// Inside the Settings modal the accent is indigo (global.css paints every
// antd primary button red).
const MODAL_ACCENT_BUTTON = { background: '#6366f1', borderColor: '#6366f1', color: '#fff' };

const Steps = ({ children }) => (
  <ol className="list-decimal pl-5 flex flex-col gap-1.5 text-sm text-gray-700">{children}</ol>
);

// compact: tighter padding and tab bar, for the Settings modal's Integrations tab.
const McpConnectGuide = ({ compact = false }) => {
  const intl = useIntl();
  const { data, isLoading, isError } = useGetMcpConnectionInfoQuery();
  const mcpUrl = data?.data?.mcpUrl || null;
  const oauthEnabled = Boolean(data?.data?.oauthEnabled);
  const pad = compact ? 'px-4 py-3' : 'px-6 py-4';

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    notification.success({
      message: intl.formatMessage({ id: 'MCP_COPIED', defaultMessage: 'Copied to clipboard' }),
      placement: 'bottomRight',
      duration: 2,
    });
  };

  const header = (
    <div className={`${pad} border-b border-gray-100`}>
      <h2 className="text-sm font-semibold text-gray-900">
        <FormattedMessage id="MCP_CONNECT_TITLE" defaultMessage="Connect an AI assistant (MCP)" />
      </h2>
      <p className="text-xs text-gray-500 mt-0.5">
        <FormattedMessage
          id="MCP_CONNECT_DESCRIPTION"
          defaultMessage="Let Claude, ChatGPT, Cursor and other AI tools read and update your Workflo tasks. You approve access once in Workflo, and you can disconnect it any time under Connected apps below."
        />
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {header}
        <div className={`${pad} text-sm text-gray-400`}>…</div>
      </div>
    );
  }

  if (isError || !mcpUrl) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {header}
        <div className={pad}>
          <Alert
            type="info"
            showIcon
            message={intl.formatMessage({
              id: 'MCP_NOT_CONFIGURED',
              defaultMessage:
                'The Workflo MCP server is not set up on this server yet. Ask your administrator to enable it.',
            })}
          />
        </div>
      </div>
    );
  }

  const cursorLink = `cursor://anysphere.cursor-deeplink/mcp/install?name=workflo&config=${encodeURIComponent(
    btoa(JSON.stringify({ url: mcpUrl }))
  )}`;
  const vscodeLink = `vscode:mcp/install?${encodeURIComponent(
    JSON.stringify({ name: 'workflo', type: 'http', url: mcpUrl })
  )}`;
  const claudeCodeCmd = `claude mcp add --transport http workflo ${mcpUrl}`;
  const cursorJson = JSON.stringify({ mcpServers: { workflo: { url: mcpUrl } } }, null, 2);
  const vscodeJson = JSON.stringify({ servers: { workflo: { type: 'http', url: mcpUrl } } }, null, 2);
  const allowStep = (
    <li>
      <FormattedMessage
        id="MCP_STEP_ALLOW"
        defaultMessage="A Workflo page opens: sign in if needed, click Allow and confirm it's you. Done."
      />
    </li>
  );

  const tabs = [
    {
      key: 'claude',
      label: 'Claude',
      children: (
        <Steps>
          <li>
            <FormattedMessage
              id="MCP_CLAUDE_STEP_1"
              defaultMessage="In claude.ai or the Claude desktop/mobile app open Settings → Connectors → Add custom connector."
            />
          </li>
          <li>
            <FormattedMessage
              id="MCP_CLAUDE_STEP_2"
              defaultMessage="Name it Workflo, paste the server address above and click Add, then Connect."
            />
          </li>
          {allowStep}
        </Steps>
      ),
    },
    {
      key: 'chatgpt',
      label: 'ChatGPT',
      children: (
        <Steps>
          <li>
            <FormattedMessage
              id="MCP_CHATGPT_STEP_1"
              defaultMessage="In ChatGPT open Settings → Apps & Connectors, turn on developer mode and choose Create."
            />
          </li>
          <li>
            <FormattedMessage
              id="MCP_CHATGPT_STEP_2"
              defaultMessage="Name it Workflo, paste the server address above, choose OAuth authentication and create it."
            />
          </li>
          {allowStep}
        </Steps>
      ),
    },
    {
      key: 'claude-code',
      label: 'Claude Code',
      children: (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-700">
            <FormattedMessage id="MCP_CLAUDE_CODE_STEP_1" defaultMessage="Run in your terminal:" />
          </p>
          <CodeBlock code={claudeCodeCmd} onCopy={copy} />
          <p className="text-sm text-gray-700">
            <FormattedMessage
              id="MCP_CLAUDE_CODE_STEP_2"
              defaultMessage="Then in Claude Code type /mcp, choose workflo → Authenticate, and click Allow on the Workflo page."
            />
          </p>
        </div>
      ),
    },
    {
      key: 'cursor',
      label: 'Cursor',
      children: (
        <div className="flex flex-col gap-2">
          <div>
            <Button type={compact ? 'default' : 'primary'} style={compact ? MODAL_ACCENT_BUTTON : undefined} href={cursorLink}>
              <FormattedMessage id="MCP_ADD_TO_CURSOR" defaultMessage="Add to Cursor" />
            </Button>
          </div>
          <p className="text-sm text-gray-700">
            <FormattedMessage
              id="MCP_CURSOR_MANUAL"
              defaultMessage="Or add this to ~/.cursor/mcp.json, then connect Workflo in Cursor's MCP settings:"
            />
          </p>
          <CodeBlock code={cursorJson} onCopy={copy} />
        </div>
      ),
    },
    {
      key: 'vscode',
      label: 'VS Code',
      children: (
        <div className="flex flex-col gap-2">
          <div>
            <Button type={compact ? 'default' : 'primary'} style={compact ? MODAL_ACCENT_BUTTON : undefined} href={vscodeLink}>
              <FormattedMessage id="MCP_ADD_TO_VSCODE" defaultMessage="Add to VS Code" />
            </Button>
          </div>
          <p className="text-sm text-gray-700">
            <FormattedMessage
              id="MCP_VSCODE_MANUAL"
              defaultMessage="Or add this to .vscode/mcp.json in your project and start the server from the MCP view:"
            />
          </p>
          <CodeBlock code={vscodeJson} onCopy={copy} />
        </div>
      ),
    },
    {
      key: 'other',
      label: intl.formatMessage({ id: 'MCP_OTHER_TOOLS', defaultMessage: 'Other tools' }),
      children: (
        <p className="text-sm text-gray-700">
          <FormattedMessage
            id="MCP_OTHER_TOOLS_TEXT"
            defaultMessage="Any MCP client that supports remote servers with OAuth works with the address above. For a tool without OAuth support, create a Personal Access Token below and send it as the header Authorization: Bearer <token>."
          />
        </p>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {header}
      <div className={`${pad} flex flex-col ${compact ? 'gap-3' : 'gap-4'}`}>
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">
            <FormattedMessage id="MCP_SERVER_ADDRESS" defaultMessage="Server address" />
          </div>
          <div className="flex items-center gap-2">
            <Input readOnly value={mcpUrl} />
            <Button onClick={() => copy(mcpUrl)}>
              <FormattedMessage id="MCP_COPY" defaultMessage="Copy" />
            </Button>
          </div>
        </div>
        {!oauthEnabled && (
          <Alert
            type="warning"
            showIcon
            message={intl.formatMessage({
              id: 'MCP_OAUTH_OFF',
              defaultMessage:
                'One-click sign-in is not enabled on this server: connect with a Personal Access Token instead.',
            })}
          />
        )}
        {isLocalAddress(mcpUrl) && (
          <Alert
            type="info"
            showIcon
            message={intl.formatMessage({
              id: 'MCP_LOCAL_ADDRESS',
              defaultMessage:
                'This address only works on this computer (Claude Code, Cursor, VS Code). claude.ai and ChatGPT need a public HTTPS address.',
            })}
          />
        )}
        {/* Compact: smaller gutter so all six clients fit on one row. */}
        <Tabs items={tabs} size={compact ? 'small' : 'middle'} tabBarGutter={compact ? 18 : undefined} />
      </div>
    </div>
  );
};

export default McpConnectGuide;
